import get from 'lodash/get';
import noop from 'lodash/noop';
import React, { useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import SelectDocumentInGroup from '~/components/SearchV2/AddSearchResultsToGroupV2/SelectDocumentInGroup/SelectDocumentInGroup';
import SelectGroup from '~/components/SearchV2/AddSearchResultsToGroupV2/SelectGroup/SelectGroup';
import { AddToGroupStepType } from '~/enums/AddToGroupType';
import { HttpStatusCodeType } from '~/enums/HttpStatusCodeType';
import { useIntl } from 'react-intl';
import { api } from '~/redux/api/createApi';
import { slices } from '~/redux/slices';
import { ERROR, SUCCESS } from '~/types/toast.types';
import LoadingContainer from '~/ui/components/LoadingContainer/LoadingContainer';
import WizardPageLayout from '~/ui/components/WizardPageLayout/WizardPageLayout';

import { showToast } from '../../Shared/EcToast/showToast';

const MAX_TITLE_WIDTH = 355;

const AddToGroup = ({
  hideModal = noop,
  documentIds,
  folderId,
  reloadDocumentContent = noop,
}) => {
  const intl = useIntl();
  const dispatch = useDispatch();

  const step = useSelector(
    slices.documents.selectors.selectCurrentAddToGroupStep,
  );
  const selectedGroupId = useSelector(
    slices.documents.selectors.selectSelectedGroupId,
  );
  const selectedDocumentId = useSelector(
    slices.documents.selectors.selectSelectedDocumentId,
  );

  const [addDocumentsToGroup, addDocumentsToGroupResult] =
    api.endpoints.addDocumentsToGroup.useMutation();
  const {
    isError: addDocumentsToGroupError,
    isSuccess: addDocumentsToGroupSuccess,
    isLoading: addDocumentsToGroupLoading,
    error: addDocumentsToGroupResponseError,
  } = addDocumentsToGroupResult;

  const [addDocumentsToGroupLinkParent, addDocumentsToGroupLinkParentResult] =
    api.endpoints.addDocumentsToGroupLinkParent.useMutation();
  const {
    isError: addDocumentsToGroupLinkParentError,
    isSuccess: addDocumentsToGroupLinkParentSuccess,
    isLoading: addDocumentsToGroupLinkParentLoading,
    error: addDocumentsToGroupLinkParentResponseError,
  } = addDocumentsToGroupLinkParentResult;

  const [addDocumentsToGroupByFolder, addDocumentsToGroupByFolderResult] =
    api.endpoints.addDocumentsToGroupByFolder.useMutation();

  const {
    isError: addDocumentsToGroupByFolderError,
    isSuccess: addDocumentsToGroupByFolderSuccess,
    isLoading: addDocumentsToGroupByFolderLoading,
    error: addDocumentsToGroupByFolderResponseError,
  } = addDocumentsToGroupByFolderResult;

  const [createDocumentGroup, createDocumentGroupResult] =
    api.endpoints.createDocumentGroup.useMutation();
  const {
    isError: createDocumentGroupError,
    isSuccess: createDocumentGroupSuccess,
    isLoading: createDocumentGroupLoading,
    error: createDocumentGroupResponseError,
    data: createDocumentGroupData,
  } = createDocumentGroupResult;

  const resetAddToGroupModal = () => {
    hideModal();
    dispatch(
      slices.documents.actions.setCurrentAddToGroupStep(
        AddToGroupStepType.GROUP,
      ),
    );
    dispatch(slices.documents.actions.setSelectedDocumentId(undefined));
  };

  useEffect(() => {
    if (createDocumentGroupSuccess) {
      handleAddDocumentsToGroup(createDocumentGroupData.groupId);
    }
  }, [createDocumentGroupSuccess]);

  useEffect(() => {
    if (
      addDocumentsToGroupSuccess ||
      addDocumentsToGroupLinkParentSuccess ||
      addDocumentsToGroupByFolderSuccess
    ) {
      showToast(
        SUCCESS,
        intl.formatMessage({
          id: 'AddToGroup.documentsAddedSuccess',
          defaultMessage: 'Documents added to the group successfully!',
          description: 'Success message when documents are added to group',
        }),
      );
      resetAddToGroupModal();
      reloadDocumentContent();
    }
  }, [
    addDocumentsToGroupSuccess,
    addDocumentsToGroupLinkParentSuccess,
    addDocumentsToGroupByFolderSuccess,
  ]);

  useEffect(() => {
    if (
      addDocumentsToGroupError ||
      addDocumentsToGroupLinkParentError ||
      addDocumentsToGroupByFolderError ||
      createDocumentGroupError
    ) {
      const error =
        addDocumentsToGroupResponseError ||
        addDocumentsToGroupLinkParentResponseError ||
        addDocumentsToGroupByFolderResponseError ||
        createDocumentGroupResponseError;
      const { response } = error;
      const defaultErrorMessage = intl.formatMessage({
        id: 'AddToGroup.defaultErrorMessage',
        defaultMessage:
          'Error on adding documents to the group, please try again later!',
        description: 'Default error message for add to group failures',
      });
      if (response.status === HttpStatusCodeType.BadRequest) {
        showToast(ERROR, get(response, 'data.message', defaultErrorMessage));
      } else {
        showToast(ERROR, defaultErrorMessage);
      }
      resetAddToGroupModal();
    }
  }, [
    addDocumentsToGroupError,
    addDocumentsToGroupLinkParentError,
    addDocumentsToGroupByFolderError,
    createDocumentGroupError,
  ]);

  const handleSelectGroup = useCallback((groupId) => {
    dispatch(
      slices.documents.actions.setCurrentAddToGroupStep(
        AddToGroupStepType.STRUCTURE,
      ),
    );
    dispatch(slices.documents.actions.setSelectedGroupId(groupId));
  }, []);

  const handleSelectedDocument = useCallback((documentId) => {
    dispatch(slices.documents.actions.setSelectedDocumentId(documentId));
  }, []);

  const handleAddDocumentsToGroup = (groupId) => {
    if (selectedDocumentId) {
      addDocumentsToGroupLinkParent({
        groupId: selectedGroupId || groupId,
        parentId: selectedDocumentId,
        documentIds,
        folder: folderId,
      });
    } else {
      if (folderId) {
        addDocumentsToGroupByFolder({
          groupId: selectedGroupId || groupId,
          folder: folderId,
        });
      } else {
        addDocumentsToGroup({
          groupId: selectedGroupId || groupId,
          documentIds,
        });
      }
    }
    dispatch(slices.documents.actions.setSelectedDocumentId(undefined));
    dispatch(slices.documents.actions.setSelectedGroupId(undefined));
  };

  const handleNewGroupCreated = ({ name, note }) => {
    createDocumentGroup({ name, note });
  };

  const steps = useMemo(() => {
    return {
      [AddToGroupStepType.GROUP]: {
        title: intl.formatMessage({
          id: 'AddToGroup.addingDocumentsTitle',
          defaultMessage: 'Adding documents to group',
          description: 'Title for step 1 of add to group process',
        }),
        component: (
          <SelectGroup
            onSelectGroup={handleSelectGroup}
            onNewGroupCreated={handleNewGroupCreated}
          />
        ),
        action: {
          promise: () => resetAddToGroupModal(),
          text: intl.formatMessage({
            id: 'AddToGroup.cancelButton',
            defaultMessage: 'Cancel',
            description: 'Cancel button text',
          }),
          variant: 'ghost',
        },
      },
      [AddToGroupStepType.STRUCTURE]: {
        title: intl.formatMessage({
          id: 'AddToGroup.addToGroupTitle',
          defaultMessage: 'Add to group',
          description: 'Title for step 2 of add to group process',
        }),
        component: (
          <SelectDocumentInGroup
            group={selectedGroupId}
            onDocumentSelected={handleSelectedDocument}
          />
        ),
        action: {
          text: intl.formatMessage({
            id: 'AddToGroup.addToGroupButton',
            defaultMessage: 'Add to group',
            description: 'Add to group button text',
          }),
        },
      },
    };
  }, [selectedGroupId]);

  const isLoading =
    addDocumentsToGroupLoading ||
    addDocumentsToGroupLinkParentLoading ||
    addDocumentsToGroupByFolderLoading ||
    createDocumentGroupLoading;

  const buttonTitle = selectedDocumentId
    ? intl.formatMessage({
        id: 'AddToGroup.addAsLinkedDocumentsButton',
        defaultMessage: 'Add to group as linked documents',
        description: 'Button text for adding as linked documents',
      })
    : steps[step].action.text;
  const handleActionPromise =
    step === AddToGroupStepType.STRUCTURE
      ? handleAddDocumentsToGroup
      : steps[step].action.promise;
  return (
    <WizardPageLayout
      currentStep={step}
      steps={[AddToGroupStepType.GROUP, AddToGroupStepType.STRUCTURE]}
      title={{ text: steps[step].title, maxWidth: MAX_TITLE_WIDTH }}
      onClose={resetAddToGroupModal}
      actionButton={{
        ...steps[step].action,
        text: buttonTitle,
        promise: handleActionPromise,
      }}
      isContentFill={true}
    >
      <LoadingContainer isLoading={isLoading}>
        {steps[step].component}
      </LoadingContainer>
    </WizardPageLayout>
  );
};

export default AddToGroup;
